import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { Upload, X } from "react-feather";
import CustomAudioPlayer from "../../../components/CustomAudioPlayer";

/**
 * The message a family actually receives: text for the SMS, audio and spoken words for the call,
 * subject and body for the email. Same fields as the Messages screen, so what gets saved here
 * shows up there too.
 *
 * @param {{
 *  value: object;
 *  onChange: (message: object) => void;
 *  audioFile?: File;
 *  onAudioChange: (file: File | null) => void;
 *  idPrefix: string;
 * }} props
 */
function MessageFields({ value, onChange, audioFile, onAudioChange, idPrefix }) {
  const [fileObjectUrl, setFileObjectUrl] = useState();
  const fileObjectUrlRef = useRef();
  fileObjectUrlRef.current = fileObjectUrl;

  useEffect(() => {
    return () => {
      fileObjectUrlRef.current && URL.revokeObjectURL(fileObjectUrlRef.current);
    };
  }, []);

  const handleChange = (event) =>
    onChange({ ...value, [event.target.name]: event.target.value });

  const onSelectFile = (e) => {
    if (!e.target.files?.length) {
      onAudioChange(null);
      return;
    }
    onAudioChange(e.target.files[0]);
    fileObjectUrl && URL.revokeObjectURL(fileObjectUrl);
    setFileObjectUrl(URL.createObjectURL(e.target.files[0]));
    e.target.value = null;
  };

  const clearFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onAudioChange(null);
    fileObjectUrl && URL.revokeObjectURL(fileObjectUrl);
    setFileObjectUrl(null);
  };

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Text message</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="What the family gets as a text"
          name="message"
          value={value.message || ""}
          onChange={handleChange}
        />
        <Form.Text muted>
          Families set to text get this. &ldquo;Masbia&rdquo; is added at the
          front automatically if you don&rsquo;t mention it.
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Recording for the phone call</Form.Label>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            {(fileObjectUrl || value.playFileUrl) && (
              <CustomAudioPlayer src={fileObjectUrl || value.playFileUrl} />
            )}
          </div>
          <input
            id={`${idPrefix}-audio-upload`}
            type="file"
            accept=".mp3,.wav"
            hidden
            onChange={onSelectFile}
          />
          <label
            htmlFor={`${idPrefix}-audio-upload`}
            className={`btn btn-${audioFile ? "success" : "light"} mb-0`}
            style={{ cursor: "pointer" }}
          >
            <span className="me-2">
              {audioFile ? audioFile.name : "Upload audio (MP3 or WAV)"}
            </span>
            {audioFile ? <X onClick={clearFile} /> : <Upload size={18} />}
          </label>
        </div>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Spoken words on the call</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          placeholder="Read out after the recording, or instead of it if there is no recording"
          name="say"
          value={value.say || ""}
          onChange={handleChange}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Email subject</Form.Label>
        <Form.Control
          type="text"
          name="emailSubject"
          value={value.emailSubject || ""}
          onChange={handleChange}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Email body</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="emailBody"
          value={value.emailBody || ""}
          onChange={handleChange}
        />
      </Form.Group>
    </>
  );
}

export default MessageFields;
